import { Navigate, useParams } from "react-router-dom";
import { EmptyState, LoadingSkeleton } from "@a2z/ui";
import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { PERMISSIONS, hasPermission, useSession } from "@/modules/auth/useSession";
import { useLogout } from "@/modules/auth/useLogout";
import type { RestaurantSettings } from "@/lib/api/types";
import { ManagePage } from "./ManagePage";
import { manageLandingPath } from "./rows";

/**
 * Where bare `/manage` lands, per role.
 *
 * It is derived from the SAME row list `ManageShell` renders (`rows.ts`) — not
 * from a list of its own. It used to carry one: five hardcoded candidates and a
 * `?? "invoices"` fallback, against a nav of thirteen rows. Every row the copy
 * did not know about was a session redirected onto a permission wall while its
 * own row sat in the sidebar beside it — a REPORT_VIEW-only owner's analyst, a
 * SHIFT_MANAGE-only cashier, a TIER_MANAGE-only or MEMBER/CATEGORY-only role.
 * Now a row added to the nav becomes a legal landing in the same edit.
 *
 * Two rows are deliberately NOT landing targets — the till and the kitchen
 * board. Both are links OUT of the back office, and the till's own exit-X
 * points at `/manage`: a redirect back to `/pos` would make that control a
 * no-op that returns you to where you pressed it. See `isSection` in `rows.ts`.
 *
 * And when there is no landing at all, this does not redirect: it explains.
 * The old `?? "invoices"` sent a cook — who holds no `POS_USE` — to the one
 * screen guaranteed to deny them.
 */
export function ManageIndexRedirect() {
  const t = useTranslate();
  const { locale = "ar" } = useParams();
  const { data, isLoading } = useSession();
  const { logout, isLoggingOut } = useLogout();

  /*
   * The same feature toggles the shell filters its nav with, read off the same
   * query key — so this is the shell's cache entry, not a second fetch — and
   * failing OPEN for the same reason: a landing that moved because a settings
   * request was slow would be worse than one row too many.
   */
  const canReadSettings = hasPermission(data, PERMISSIONS.MENU_VIEW);
  const settingsQuery = useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
    { enabled: canReadSettings, retry: false },
  );
  const features = {
    kitchenDisplay: settingsQuery.data
      ? settingsQuery.data.kitchenDisplayEnabled
      : true,
    shiftTracking: settingsQuery.data
      ? settingsQuery.data.shiftTrackingEnabled
      : true,
  };

  // Never decide from an unread session: with no permissions yet, EVERY row is
  // invisible and the no-access screen would flash before the real answer.
  if (isLoading || !data) {
    return (
      <ManagePage hideTitle title={t((m) => m.common.appName)}>
        <div
          aria-busy="true"
          aria-live="polite"
          aria-label={t((m) => m.common.loading)}
        >
          <LoadingSkeleton rows={3} />
        </div>
      </ManagePage>
    );
  }

  const target = manageLandingPath(locale, data.seller.permissions, features);
  if (target) {
    return <Navigate to={target} replace />;
  }

  /*
   * Nothing in the back office is open to this session. The way OUT is a
   * permission question, exactly like the landing rule itself
   * (`modules/auth/landing.ts`): the board for a cook, whose whole app it is;
   * the till for anyone who works the floor; and for a session that holds
   * neither, sign-out — because there is genuinely nowhere else to send them,
   * and a screen with no exit is how someone ends up clearing cookies.
   */
  const toKitchen = hasPermission(data, PERMISSIONS.KITCHEN_VIEW);
  const toPos = hasPermission(data, PERMISSIONS.POS_USE);
  return (
    <ManagePage hideTitle title={t((m) => m.nav.manage)}>
      <EmptyState
        title={t((m) => m.manage.indexNoAccessTitle)}
        description={t((m) => m.manage.indexNoAccessBody)}
        actionLabel={
          toKitchen
            ? t((m) => m.manage.indexNoAccessToKitchen)
            : toPos
              ? t((m) => m.manage.indexNoAccessToPos)
              : t((m) => m.common.logout)
        }
        actionHref={
          toKitchen
            ? `/${locale}/kitchen`
            : toPos
              ? `/${locale}/pos`
              : undefined
        }
        onAction={toKitchen || toPos ? undefined : logout}
        actionVariant={toKitchen || toPos ? "primary" : "outline"}
      />
      <p aria-live="polite" className="sr-only">
        {isLoggingOut ? t((m) => m.common.loading) : ""}
      </p>
    </ManagePage>
  );
}
